import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import type { Breakpoint } from "@mui/material/styles";
import type { ReactNode } from "react";
import { type DefaultValues, type FieldValues, FormProvider, type Path, type Resolver, useForm, type UseFormReturn } from "react-hook-form";
import type { ObjectSchema } from "yup";

import { readFieldErrors } from "@mr-portfolio-api/api-error";
import { type GetLabel, isTranslationKey, useLabel } from "@mr-portfolio-infrastructure/translations/use-label";
import { useStores } from "@mr-portfolio-store/use-stores";

import { AsyncButton, AsyncButtonStartIconValues } from "../async-button";
import type { HookFormProps } from "../smart-hook-form";
import { BaseDialog } from "./base-dialog";

export interface BaseFormDialogProps<TValues extends FieldValues> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  contentText?: ReactNode;
  initialValues: TValues;
  validationSchema?: ObjectSchema<TValues>;
  onSubmit: (values: TValues) => Promise<void>;
  children: (formProps: HookFormProps<TValues>) => ReactNode;
  okButtonTitle?: ReactNode;
  cancelButtonTitle?: ReactNode;
  maxWidth?: Breakpoint | false;
}

export function BaseFormDialog<TValues extends FieldValues>(props: BaseFormDialogProps<TValues>): React.JSX.Element {
  const {
    open,
    onClose,
    title,
    contentText,
    initialValues,
    validationSchema,
    onSubmit,
    children,
    okButtonTitle,
    cancelButtonTitle,
    maxWidth = "sm",
  } = props;
  const getLabel = useLabel();
  const { notificationStore } = useStores();

  const formReturn = useForm<TValues>({
    defaultValues: initialValues as DefaultValues<TValues>,
    ...(validationSchema !== undefined ? { resolver: yupResolver(validationSchema) as unknown as Resolver<TValues> } : {}),
  });

  const submitForm = formReturn.handleSubmit(async (values: TValues) => {
    try {
      await onSubmit(values);
      onClose();
    } catch (submitError: unknown) {
      const isShownOnFields = showFieldErrors(formReturn, readFieldErrors(submitError), getLabel);

      if (!isShownOnFields) {
        notificationStore.showError("notifications.requestFailed");
      }
    }
  });

  const formProps: HookFormProps<TValues> = {
    formReturn,
    control: formReturn.control,
    submitForm,
    setFieldValue: (fieldName, value) =>
      formReturn.setValue(fieldName, value, { shouldDirty: true, shouldValidate: formReturn.formState.isSubmitted }),
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={title}
      contentText={contentText}
      maxWidth={maxWidth}
      cancelButtonTitle={cancelButtonTitle ?? getLabel("app.buttons.cancel")}
      disableBackdropClickClose
      additionalActions={
        <AsyncButton variant="contained" startIconValue={AsyncButtonStartIconValues.SaveIconValue} onClick={submitForm}>
          {okButtonTitle ?? getLabel("app.buttons.save")}
        </AsyncButton>
      }
    >
      <FormProvider {...formReturn}>
        <form
          noValidate
          onSubmit={(event) => {
            void submitForm(event);
          }}
        >
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {children(formProps)}
          </Grid>
        </form>
      </FormProvider>
    </BaseDialog>
  );
}

/** Puts the server's messages on the matching form fields; false when none of them belongs to a field of the form. */
function showFieldErrors<TValues extends FieldValues>(
  formReturn: UseFormReturn<TValues>,
  fieldErrors: Record<string, string>,
  getLabel: GetLabel,
): boolean {
  const formFieldNames = Object.keys(formReturn.getValues());
  const formFieldErrors = Object.entries(fieldErrors).filter(([fieldName]) => formFieldNames.includes(fieldName));

  formFieldErrors.forEach(([fieldName, message]) => {
    formReturn.setError(fieldName as Path<TValues>, { type: "server", message: isTranslationKey(message) ? getLabel(message) : message });
  });

  const isShownOnFields = formFieldErrors.length > 0;

  return isShownOnFields;
}
